import clsx from 'clsx';
import RiskBadge from './RiskBadge';

interface PricingResult {
  group: { label: string; category: string };
  count: number;
  meanRateSpread: number;
  medianRateSpread: number;
  controlGroupMeanRateSpread: number;
  controlGroupMedianRateSpread: number;
  meanDifferenceBps: number;
  pValue: number | null;
  testUsed: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  isStatisticallySignificant: boolean;
  sampleSizeWarning: boolean;
}

const RISK_COLORS = {
  LOW: 'bg-green-500',
  MEDIUM: 'bg-amber-500',
  HIGH: 'bg-red-500',
};

export default function PricingChart({ results }: { results: PricingResult[] }) {
  if (!results || results.length === 0) {
    return <p className="text-sm text-gray-500">No pricing data available.</p>;
  }

  const maxBps = Math.max(...results.map(r => Math.abs(r.meanDifferenceBps)), 60);

  return (
    <div className="space-y-6">
      {/* Chart — horizontal bars showing BPS difference from control */}
      <div className="space-y-3">
        {results.map((r, i) => {
          const absBps = Math.abs(r.meanDifferenceBps);
          const barWidth = Math.min((absBps / maxBps) * 100, 100);
          return (
            <div key={i} className="flex items-center gap-3">
              <div className="w-48 text-sm text-gray-700 truncate text-right flex-shrink-0">
                {r.group.label}
              </div>
              <div className="flex-1 relative">
                <div className="h-7 bg-gray-100 rounded-md relative overflow-hidden">
                  <div
                    className={clsx('h-full rounded-md transition-all', RISK_COLORS[r.riskLevel])}
                    style={{ width: `${barWidth}%` }}
                  />
                  {/* 25 bps threshold */}
                  <div
                    className="absolute top-0 bottom-0 border-l-2 border-dashed border-amber-400"
                    style={{ left: `${Math.min((25 / maxBps) * 100, 100)}%` }}
                    title="25 bps threshold"
                  />
                  {/* 50 bps threshold */}
                  <div
                    className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-400"
                    style={{ left: `${Math.min((50 / maxBps) * 100, 100)}%` }}
                    title="50 bps threshold"
                  />
                </div>
              </div>
              <div className="w-32 text-xs text-gray-500 flex-shrink-0">
                <span className={clsx('font-semibold', r.meanDifferenceBps > 0 ? 'text-red-700' : 'text-green-700')}>
                  {r.meanDifferenceBps > 0 ? '+' : ''}{r.meanDifferenceBps.toFixed(0)} bps
                </span>
                {r.sampleSizeWarning && <span className="ml-1 text-amber-500">⚠</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-4 border-t-2 border-dashed border-amber-400" /> 25 bps
        </span>
        <span className="flex items-center gap-1">
          <span className="w-4 border-t-2 border-dashed border-red-400" /> 50 bps
        </span>
      </div>

      {/* Detail table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b">
              <th className="py-2 pr-4">Group</th>
              <th className="py-2 pr-4">Loans</th>
              <th className="py-2 pr-4">Mean Spread</th>
              <th className="py-2 pr-4">Control Mean</th>
              <th className="py-2 pr-4">Diff (bps)</th>
              <th className="py-2 pr-4">p-value</th>
              <th className="py-2 pr-4">Risk</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {results.map((r, i) => (
              <tr key={i}>
                <td className="py-2 pr-4 font-medium text-gray-900">{r.group.label}</td>
                <td className="py-2 pr-4 text-gray-600">{r.count}</td>
                <td className="py-2 pr-4 text-gray-600">{r.meanRateSpread.toFixed(3)}</td>
                <td className="py-2 pr-4 text-gray-600">{r.controlGroupMeanRateSpread.toFixed(3)}</td>
                <td className="py-2 pr-4 font-semibold">
                  {r.meanDifferenceBps > 0 ? '+' : ''}{r.meanDifferenceBps.toFixed(0)}
                  {r.sampleSizeWarning && <span className="ml-1 text-amber-500">⚠</span>}
                </td>
                <td className="py-2 pr-4 text-gray-600">
                  {r.pValue !== null ? r.pValue.toFixed(4) : 'N/A'}
                  {r.isStatisticallySignificant && <span className="ml-1 text-red-500">*</span>}
                </td>
                <td className="py-2 pr-4"><RiskBadge level={r.riskLevel} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
